import { useCallback, useEffect, useRef, useState } from "react"
import { Camera, Hand, ImageUp, RotateCcw } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useMediaStore } from "@/stores/media-store"
import type { StepComponentProps } from "./step-contract"

/** Marker stored in the answers so the step validates; the bytes live in the media store. */
export const PHOTO_CAPTURED = "captured"

type CameraError = "denied" | "unavailable" | null

export function PhotoStep({ step, onChange }: StepComponentProps) {
  const facing = step.cameraFacing ?? "environment"
  const photo = useMediaStore((s) => s.photos[step.id])
  const setPhoto = useMediaStore((s) => s.setPhoto)
  const clearPhoto = useMediaStore((s) => s.clearPhoto)

  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  // Capability is known at render time, so seed it here rather than via a
  // synchronous setState inside the effect.
  const [error, setError] = useState<CameraError>(() =>
    typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function"
      ? null
      : "unavailable",
  )

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } })
      streamRef.current = stream
      setError(null)
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => {})
      }
    } catch {
      setError("denied")
    }
  }, [facing])

  // Run the camera while in capture mode; tear it down on photo/unmount. The
  // camera is an external system this effect syncs to; setError only fires
  // asynchronously after the getUserMedia promise settles, never synchronously.
  useEffect(() => {
    if (photo) {
      stopCamera()
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async external-system sync
    startCamera()
    return stopCamera
  }, [photo, startCamera, stopCamera])

  const store = (dataUrl: string) => {
    setPhoto(step.id, dataUrl)
    onChange(PHOTO_CAPTURED)
  }

  const capture = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    store(canvas.toDataURL("image/jpeg", 0.85))
  }

  const retake = () => {
    clearPhoto(step.id)
    onChange("")
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => typeof reader.result === "string" && store(reader.result)
    reader.readAsDataURL(file)
  }

  const frameClass = "relative aspect-[3/4] w-full overflow-hidden rounded-3xl bg-purple-light/10"

  // Preview of the captured photo (Continue lives in the renderer footer).
  if (photo) {
    return (
      <div className="flex flex-1 flex-col">
        <div className={frameClass}>
          <img src={photo} alt="Your photo" className="size-full object-cover" />
        </div>
        <Button
          variant="ghost"
          size="xl"
          onClick={retake}
          className="mt-4 bg-purple-light/10 text-purple-light hover:bg-purple-light/15"
        >
          <RotateCcw className="size-5" />
          Retake photo
        </Button>
      </div>
    )
  }

  // Camera blocked/unsupported → fall back to the system photo picker.
  if (error) {
    return (
      <div className="flex flex-1 flex-col">
        <div
          className={cn(
            frameClass,
            "flex flex-col items-center justify-center gap-3 px-6 text-center",
          )}
        >
          <Hand className="size-10 text-purple-light/50" />
          <p className="text-sm text-purple-light/70">
            {error === "denied"
              ? "Camera access is off. Upload a photo of your palm instead."
              : "Camera isn't available here. Upload a photo of your palm instead."}
          </p>
        </div>
        <label className="mt-4">
          <input
            type="file"
            accept="image/*"
            capture={facing}
            onChange={handleFile}
            className="sr-only"
          />
          <span className="flex h-14 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-main-purple text-base font-medium text-white transition-colors hover:bg-main-purple/90">
            <ImageUp className="size-5" />
            Upload a photo
          </span>
        </label>
      </div>
    )
  }

  // Live camera with a palm guide and a shutter.
  return (
    <div className="flex flex-1 flex-col">
      <div className={frameClass}>
        <video ref={videoRef} autoPlay playsInline muted className="size-full object-cover" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-6 rounded-2xl border-2 border-dashed border-purple-light/60"
        />
      </div>
      <button
        type="button"
        onClick={capture}
        aria-label="Take photo"
        className="mx-auto mt-6 flex size-16 items-center justify-center rounded-full bg-main-purple text-white shadow-lg transition-transform active:scale-95"
      >
        <Camera className="size-7" />
      </button>
    </div>
  )
}
